import { styled } from '@mui/material';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ControlPanel from '../components/ControlPanel/ControlPanel';
import ControlsPractitioner from '../components/ControlsPractitioner/ControlsPractitioner';
import LocalVideoPreview from '../components/LocalVideoPreview/LocalVideoPreview';
import PractitionerControlsProvider from '../components/PractionerControlsProvider';
import PractitionerMediaPreviewPlayer from '../components/PractitionerMediaPreviewPlayer/PractitionerMediaPreviewPlayer';
import ReconnectingNotification from '../components/ReconnectingNotification/ReconnectingNotification';
import Room from '../components/Room/Room';
import VideoLoader from '../components/VideoLoader/VideoLoader';
import useHandleOnDisconnect from '../components/VideoProvider/useHandleOnDisconnect/useHandleOnDisconnect';
import useAppContext from '../hooks/useAppContext/useAppContext';
import useAuthorizedPractitioner from '../hooks/useAuthorizedPractitioner/useAuthorizedPractitioner';
import useCurrentSessionState from '../hooks/useCurrentSessionState/useCurrentSessionState';
import useRoomState from '../hooks/useRoomState/useRoomState';
import useVideoContext from '../hooks/useVideoContext/useVideoContext';
import useVideoViewToggle from '../hooks/useVideoViewToggle/useVideoViewToggle';
import './PractitionerPage.scss';
import BackendService from '../services/BackendService';
import MediaProvider from '../components/MediaProvider';
import UploadProvider from '../components/MediaUploadProvider';
import SessionCheckDialog from '../components/SessionCheckDialog/SessionCheckDialog';
import * as Sentry from '@sentry/react';
const Container = styled('div')({
  display: 'flex',
  gridTemplateRows: 'auto 1fr',
});

const Main = styled('main')({
  overflow: 'hidden',
  width: '75%',
  height: '100%',
  position: 'relative',

  '& .practitioner-preview .player-container': {
    '& .player-overlay': {
      paddingTop: '56.25%',
      bottom: 'unset',
      '& .react-player': {
        position: 'absolute',
        top: 0,
        left: 0,
        minHeight: 'unset',
      },
    },
    '& .player-overlay-image': {
      position: 'absolute',
      top: 0,
      left: 0,
      minHeight: 'unset',
    },
  },
});

const Videoframe = styled('div')({
  height: '100%',
  width: '100%',
});

let isConnected: boolean = false;

function onDisconnect() {
  isConnected = false;
}

interface IVideoViewProps {
  isEnabled: boolean;
}

function ToggleVideoView(props: IVideoViewProps) {
  return props.isEnabled ? <Video /> : null;
}

const Video = () => {
  const roomState = useRoomState();

  return (
    <div style={{ height: '100%', width: '100%' }}>
      <div style={{ position: 'absolute', zIndex: 1, width: '250px', bottom: '80px' }}>
        <LocalVideoPreview />
      </div>
      <Videoframe>{roomState === 'disconnected' ? <LocalVideoPreview /> : <Room />}</Videoframe>
      <ReconnectingNotification />
    </div>
  );
};

// Use a robust viewport height that matches the visible area on mobile/tablet
function useViewportSize() {
  const [size, setSize] = useState<{ width: string; height: string }>(() => ({
    width: '100vw',
    height: 'calc(var(--app-vh, 1vh) * 100)',
  }));

  useEffect(() => {
    const update = () => {
      const vh = (window.visualViewport?.height ?? window.innerHeight) * 0.01;
      document.documentElement.style.setProperty('--app-vh', `${vh}px`);
      setSize({ width: '100vw', height: 'calc(var(--app-vh, 1vh) * 100)' });
    };
    update();
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);
    return () => {
      window.removeEventListener('resize', update);
      // @ts-ignore types for VisualViewport event target exist in modern TS DOM libs
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, []);

  return size;
}

export default function PractitionerPage(props: any) {
  useAuthorizedPractitioner();
  const { sessionId, treatmentId } = useAppContext();
  const [practitionerIsConnected, setPractitionerIsConnected] = useState<boolean>(false);
  const { t } = useTranslation();
  const { width, height } = useViewportSize();
  const { isEnabled, toggleVideoViewEnabled, setVideoViewState } = useVideoViewToggle(false);
  const { room, connect, localTracks: localVal } = useVideoContext();

  useHandleOnDisconnect(room, onDisconnect);
  const [collapsed, setCollapsed] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const currentState = useCurrentSessionState() || {};
  const reconnectIntervalRef = useRef<any | null>(null);
  const handleDisconnectUser = useCallback(() => {
    if (room && typeof room.disconnect === 'function') {
      room.disconnect();
      setPractitionerIsConnected(false);
      setVideoViewState(false);
    }
  }, [room, setVideoViewState]);

  const [showPermissionReminder, setShowPermissionReminder] = useState(false);
  const [token, setToken] = useState<string>('');

  useEffect(() => {
    const handleParticipantDisconnected = (participant: any) => {
      // console.log(`${participant.identity} disconnected`);
    };

    // Recommended cleanup on browser unload
    window.addEventListener('unload', handleDisconnectUser);

    // Only add listeners if room exists
    if (room) {
      room.on('participantDisconnected', handleParticipantDisconnected);
    }

    return () => {
      window.removeEventListener('unload', handleDisconnectUser);

      if (room) {
        room.off('participantDisconnected', handleParticipantDisconnected);
      }
    };
  }, [room, handleDisconnectUser]);

  useEffect(() => {
    const resetPLayerState = async () => {
      await BackendService.sendMediaVisibility(sessionId, treatmentId, '', false);
      await BackendService.sendMediaCommand(sessionId, treatmentId, '', 1, 0);
    };
    resetPLayerState();
  }, [sessionId, treatmentId]);

  const ConnectToRoom = async (token: string) => {
    // 1) Ensure camera + mic permission

    // 2) Show the “connecting” spinner and reveal the video view
    setIsConnecting(true);
    toggleVideoViewEnabled();

    // 3) Gather all pre‑created tracks (audio, video, data) from context
    const tracks = [...localVal]; // localVal comes from useVideoContext().localTracks

    try {
      // 4) Connect, forcing H264 so Safari ↔ Chrome codec matches on the first handshake
      await connect(token, {
        tracks,
        preferredVideoCodecs: ['H264'],
      });

      // 5) On success, mark both practitioner and client as connected
      isConnected = true;
      setPractitionerIsConnected(true);

      // 6) Clear any pending reconnect timer
      if (reconnectIntervalRef.current) {
        clearInterval(reconnectIntervalRef.current);
        reconnectIntervalRef.current = null;
      }
    } catch (error) {
      // 7) On failure, log and reset connection flags
      console.error('[Twilio Connect Error]', error);
      isConnected = false;
      setPractitionerIsConnected(false);
    } finally {
      // 8) Always hide the “connecting” spinner
      setIsConnecting(false);
    }
  };

  function StopCall() {
    if (room && typeof room.disconnect === 'function') {
      room.disconnect();
    }
    toggleVideoViewEnabled();
  }

  return (
    <Container style={{ height, width }}>
      <PractitionerControlsProvider>
        <MediaProvider>
          <UploadProvider>
            <VideoLoader isLoading={isConnecting} />
            {collapsed ? null : (
              <Main className={'main'}>
                <ToggleVideoView isEnabled={isEnabled} />
                {!isEnabled && <PractitionerMediaPreviewPlayer className={'practitioner-preview'} />}
              </Main>
            )}
            <ControlsPractitioner currentState={currentState} />
            <div className={`${collapsed ? 'right-full-width' : 'right'}`}>
              {/*<CollapseToggle isCollapsed={collapsed} handleCollapse={() => setCollapsed(!collapsed)} />*/}
              <ControlPanel
                ConnectToRoom={ConnectToRoom}
                StopCall={StopCall}
                practitionerIsConnected={practitionerIsConnected}
                setPractitionerIsConnected={setPractitionerIsConnected}
                currentState={currentState}
                isCallActive={isEnabled}
              />
            </div>
            <SessionCheckDialog sessionId={sessionId} treatmentId={treatmentId} />
          </UploadProvider>
        </MediaProvider>
      </PractitionerControlsProvider>
    </Container>
  );
}
